import React, { useEffect, useState } from 'react';
import { getMyDonationHistory } from '../../services/donationService';
import { Donation } from '../../types/interfaces';

const DonationHistoryView: React.FC = () => {
  const [donations, setDonations] = useState<Donation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const itemsPerPage = 10; // Or make this configurable

  useEffect(() => {
    const fetchHistory = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getMyDonationHistory(currentPage, itemsPerPage);
        setDonations(data.donations);
        setTotalPages(data.pages);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load donation history.');
        setDonations([]); // Clear donations on error
        setTotalPages(0);
      }
      setIsLoading(false);
    };

    fetchHistory();
  }, [currentPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading donation history...</span>
        </div>
        <p className="mt-2">Loading donation history...</p>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">Error: {error}</div>;
  }

  if (donations.length === 0) {
    return <p>You have not made any donations yet.</p>;
  }

  return (
    <div className="donation-history-view">
      <h4 className="mb-3">My Donation History</h4>
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Date</th>
              <th>Amount</th>
              <th>Campaign</th>
              <th>Payment Method</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {donations.map((donation) => (
              <tr key={donation._id}>
                <td>{new Date(donation.donationDate).toLocaleDateString()}</td>
                <td>{donation.currency} {donation.amount.toFixed(2)}</td>
                <td>{donation.campaignName || 'N/A'}</td>
                <td>{donation.paymentMethod || 'N/A'}</td>
                <td>{donation.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <nav aria-label="Donation history pagination">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                Previous
              </button>
            </li>
            {Array.from(Array(totalPages).keys()).map(num => (
              <li key={num + 1} className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => handlePageChange(num + 1)}>
                  {num + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default DonationHistoryView; 